import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface FieldDefinition {
  id: string;
  fieldName: string;
  displayName: string;
  fieldType: string;
  isRequired: boolean;
}

interface GateGap {
  fieldName: string;
  displayName: string;
  requirementType: string;
  isDeferrable: boolean;
}

interface GateCheckResponse {
  passed: boolean;
  gaps: GateGap[];
}

interface Props {
  opportunityId: string;
  targetStageId: string;
  targetStageName: string;
  onClose: () => void;
  onAdvanced: () => void;
}

/**
 * Story 54.3 — when the rep clicks "Advance to <stage>", we pre-flight
 * the gate check. If the target stage requires fields that are still
 * empty, we surface them inline so the rep can fill them in without
 * navigating away.
 *
 * Flow:
 *   1. GET /gate-check/{oppId}/{stageId}
 *   2. passed && no gaps -> advance immediately
 *   3. blocked or gaps -> show modal with inputs
 *   4. on save -> PUT /custom-fields/values + advance
 */
export function StageAdvanceModal({
  opportunityId,
  targetStageId,
  targetStageName,
  onClose,
  onAdvanced,
}: Props) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<'checking' | 'fillin' | 'advancing' | 'error'>('checking');
  const [gaps, setGaps] = useState<GateGap[]>([]);
  const [definitions, setDefinitions] = useState<Record<string, FieldDefinition>>({});
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Step 1: gate-check on mount.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiClient.get<GateCheckResponse>(
        `/gate-check/${opportunityId}/${targetStageId}`,
      ),
      apiClient.get<FieldDefinition[]>('/custom-fields/definitions', {
        params: { entityType: 'OPPORTUNITY' },
      }),
    ])
      .then(([gateRes, defsRes]) => {
        if (cancelled) return;
        const defMap: Record<string, FieldDefinition> = {};
        defsRes.data.forEach((d) => {
          defMap[d.fieldName] = d;
        });
        setDefinitions(defMap);
        const blockingGaps = gateRes.data.gaps;
        if (gateRes.data.passed && blockingGaps.length === 0) {
          // Nothing to do — just advance.
          performAdvance();
        } else {
          setGaps(blockingGaps);
          setPhase('fillin');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setErrorMsg(t('stageAdvance.error_check'));
        setPhase('error');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityId, targetStageId]);

  async function performAdvance() {
    setPhase('advancing');
    setErrorMsg(null);
    try {
      await apiClient.post(`/opportunities/${opportunityId}/advance`, {
        targetStageId,
      });
      onAdvanced();
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t('stageAdvance.error_advance');
      setErrorMsg(msg);
      setPhase('error');
    }
  }

  async function saveAndAdvance() {
    // Only post the values that are filled in. Empty mandatory
    // fields still trip the gate, surfacing the same modal again
    // — that's the right UX (the rep can't sneak past a real gap).
    const payload: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(values)) {
      if (v !== null && v !== undefined && String(v).trim() !== '') {
        payload[k] = v;
      }
    }
    if (Object.keys(payload).length > 0) {
      try {
        await apiClient.put(
          `/custom-fields/values/OPPORTUNITY/${opportunityId}`,
          payload,
        );
      } catch {
        setErrorMsg(t('stageAdvance.error_save'));
        return;
      }
    }
    performAdvance();
  }

  const blockingGaps = gaps.filter(
    (g) => g.requirementType === 'MANDATORY' && !g.isDeferrable,
  );
  const optionalGaps = gaps.filter(
    (g) => g.requirementType !== 'MANDATORY' || g.isDeferrable,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-1 text-lg font-semibold">
          {t('stageAdvance.title', { stage: targetStageName })}
        </h3>
        <p className="mb-4 text-xs text-muted-foreground">
          {t('stageAdvance.subtitle')}
        </p>

        {phase === 'checking' && (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        )}

        {phase === 'advancing' && (
          <p className="text-sm text-muted-foreground">
            {t('stageAdvance.advancing')}
          </p>
        )}

        {phase === 'error' && (
          <div className="space-y-3">
            <p className="text-sm text-destructive">{errorMsg}</p>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={onClose}>
                {t('common.close')}
              </Button>
            </div>
          </div>
        )}

        {phase === 'fillin' && (
          <div className="space-y-3">
            {blockingGaps.length > 0 && (
              <div className="rounded-md border border-rose-800 bg-rose-950/40 p-3 text-xs text-rose-200">
                {t('stageAdvance.blocked_intro')}
              </div>
            )}
            {optionalGaps.length > 0 && blockingGaps.length === 0 && (
              <div className="rounded-md border border-amber-800 bg-amber-950/40 p-3 text-xs text-amber-200">
                {t('stageAdvance.optional_intro')}
              </div>
            )}

            {gaps.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {t('stageAdvance.no_gaps')}
              </p>
            )}

            <div className="space-y-3">
              {gaps.map((gap) => {
                const def = definitions[gap.fieldName];
                const isBlocking =
                  gap.requirementType === 'MANDATORY' && !gap.isDeferrable;
                return (
                  <div key={gap.fieldName} className="space-y-1">
                    <label className="flex items-center gap-2 text-xs font-medium">
                      <span>{gap.displayName}</span>
                      {isBlocking ? (
                        <span className="rounded-full bg-rose-900/40 px-1.5 py-0.5 text-[10px] font-semibold text-rose-200">
                          {t('stageAdvance.required')}
                        </span>
                      ) : (
                        <span className="rounded-full bg-amber-900/40 px-1.5 py-0.5 text-[10px] text-amber-200">
                          {gap.requirementType.toLowerCase()}
                        </span>
                      )}
                    </label>
                    {renderInput(def, values[gap.fieldName], (v) =>
                      setValues((prev) => ({ ...prev, [gap.fieldName]: v })),
                    )}
                  </div>
                );
              })}
            </div>

            {errorMsg && (
              <p className="text-xs text-destructive">{errorMsg}</p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button size="sm" variant="outline" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={saveAndAdvance}
                disabled={blockingGaps.some((g) => {
                  const v = values[g.fieldName];
                  return v === null || v === undefined || String(v).trim() === '';
                })}
              >
                {t('stageAdvance.save_and_advance')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function renderInput(
  def: FieldDefinition | undefined,
  value: unknown,
  onChange: (v: unknown) => void,
) {
  const fieldType = def?.fieldType ?? 'TEXT';
  switch (fieldType) {
    case 'BOOLEAN':
      return (
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4"
        />
      );
    case 'DATE':
      return (
        <Input
          type="date"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
    case 'INTEGER':
      return (
        <Input
          type="number"
          step="1"
          value={(value as string) ?? ''}
          onChange={(e) =>
            onChange(e.target.value ? parseInt(e.target.value) : null)
          }
        />
      );
    case 'NUMERIC':
      return (
        <Input
          type="number"
          step="0.01"
          value={(value as string) ?? ''}
          onChange={(e) =>
            onChange(e.target.value ? parseFloat(e.target.value) : null)
          }
        />
      );
    default:
      return (
        <Input
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
  }
}
